// Public client portal (magic link) — Slice 2: the whole form library.
// Link shape (HashRouter): https://site/#/portal?d=<dealId>&t=<token>
// Flow: open -> (remembered device? skip the gate) -> one-time last-name gate
// -> HOME menu of the forms for this deal's side -> pick a form -> grouped
// renderer with debounced auto-save -> submit (returns to the menu, that form
// marked done). In local dev with no working backend the page degrades to a
// fully local preview so the whole menu + every form renders in the sandbox.
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getForm,
  formsForSide,
  formsForRequest,
  groupsOf,
  blankAnswers,
  applyOverrides,
  applyOverridesAll,
  isFieldVisible,
} from "../utils/portalForms";
import {
  openPortal,
  verifyPortal,
  savePortal,
  submitPortal,
  uploadPortalFile,
} from "../utils/clientPortal";
import { loadPortalFormOverrides } from "../utils/portalFormOverrides";
import { lookupAreaInfo } from "../utils/addressLookup";

const NAVY = "#1E293B";
const IS_DEV = process.env.NODE_ENV === "development";

// Device memory — after a successful verify we keep the session token so the
// client is only asked for their last name once per device.
const sessionKey = (token) => `tesPortal:${token}`;
const readStoredSession = (token) => {
  try {
    return localStorage.getItem(sessionKey(token)) || "";
  } catch {
    return ""; // Safari private mode etc. — just re-ask
  }
};
const storeSession = (token, sessionToken) => {
  try {
    localStorage.setItem(sessionKey(token), sessionToken);
  } catch {
    /* storage unavailable — the gate will simply ask again next visit */
  }
};
const clearStoredSession = (token) => {
  try {
    localStorage.removeItem(sessionKey(token));
  } catch {
    /* ignore */
  }
};

// A save/submit rejected because the device session went stale (renewed
// elsewhere, revoked, expired) — distinct from "backend unreachable".
const isSessionError = (r) => r.status === 403 || /session/i.test(r.error || "");
// fetch threw (no HTTP status at all) — the backend never answered.
const isUnreachable = (r) => !r.ok && r.status === undefined;

// Is an answer value "filled" for progress/merge purposes?
const answerFilled = (v) => {
  if (Array.isArray(v)) return v.length > 0;
  if (v && typeof v === "object") return true; // file object
  return typeof v === "string" && v.trim() !== "";
};
// Does a form have any non-empty answer in the given map? Hidden (showIf)
// fields don't count — a stale answer under a flipped controller shouldn't
// keep the form reading "In progress".
const formHasProgress = (form, ans) =>
  !!ans &&
  form.fields.some(
    (f) =>
      f.type !== "video" &&
      isFieldVisible(f, ans) &&
      answerFilled(ans[f.key]),
  );

// Merge a fresh server forms map with the device's local edits — anything the
// client typed on this device wins over what the server last saved (used after
// a session bounce so nothing mid-typed is lost).
const mergeForms = (server, local) => {
  const out = { ...(server || {}) };
  for (const [fid, ans] of Object.entries(local || {})) {
    const typed = Object.fromEntries(
      Object.entries(ans || {}).filter(([, v]) => answerFilled(v)),
    );
    out[fid] = { ...(server?.[fid] || {}), ...typed };
  }
  return out;
};

// Normalize a client from the backend (open/verify return
// { contactId, firstName, lastName, name, email, phone, mailingAddress }) into
// the shape expandAllAboutYou() prefills from (name/fullLegalName/mobilePhone/
// currentMailingAddress). Keeps the wire contract and the form-registry keys
// decoupled — the backend can rename fields without touching portalForms.js.
const fullName = (c) =>
  c.name || [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
const normalizeClient = (c = {}) => {
  const name = fullName(c);
  return {
    ...c,
    name,
    fullLegalName: c.fullLegalName || name,
    mobilePhone: c.mobilePhone || c.phone || "",
    currentMailingAddress: c.currentMailingAddress || c.mailingAddress || "",
  };
};
const normalizeClients = (arr) =>
  (Array.isArray(arr) ? arr : []).map(normalizeClient);

// Sandbox stand-ins so per-client rendering (two "All About You" forms) is
// visible in local dev with no backend. Two clients on one deal.
const DEMO_CLIENTS = [
  {
    contactId: "demo1",
    firstName: "Sarah",
    lastName: "Johnson",
    name: "Sarah Johnson",
    email: "sarah@example.com",
    phone: "(555) 010-1234",
    mailingAddress: "123 Maple Dr",
  },
  {
    contactId: "demo2",
    firstName: "Michael",
    lastName: "Johnson",
    name: "Michael Johnson",
    email: "michael@example.com",
    phone: "(555) 010-5678",
    mailingAddress: "123 Maple Dr",
  },
];

// Utility-sheet field keys the address lookup can prefill (mirror of
// AREA_INFO_FIELDS in addressLookup.js — same keys as the utility form).
const AREA_AUTOFILL_KEYS = [
  "schoolDistrict",
  "elementary",
  "middle",
  "high",
  "electricCompany",
  "gasCompany",
  "waterCompany",
];

// Hoisted to module level so the shell keeps its identity across renders —
// defining this inside the component would give it a new identity every
// render, remounting the subtree and blurring the focused input on each
// keystroke (same trap the dashboard route comments warn about in App.js).
const Shell = ({ children }) => (
  <div style={{ minHeight: "100vh", background: "#F4F6F9" }}>
    <div
      style={{
        maxWidth: 620,
        margin: "0 auto",
        padding: "24px 16px 60px",
        fontFamily: "-apple-system,Segoe UI,Roboto,sans-serif",
        color: "#45535F",
      }}
    >
      <div
        style={{
          background: "#fff",
          border: "1px solid #E6EAEF",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(15,23,42,.06)",
        }}
      >
        <div
          style={{
            background: NAVY,
            padding: "20px 24px",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ letterSpacing: 2, fontWeight: 600 }}>
            TEAM EDELSTEIN
            <div style={{ fontSize: 9.5, letterSpacing: 1.4, color: "#8FA0B4" }}>
              COLDWELL BANKER REALTY
            </div>
          </div>
          <span
            style={{
              fontSize: 11.5,
              color: "#B7E7D5",
              background: "rgba(93,202,165,.16)",
              border: "1px solid rgba(93,202,165,.3)",
              borderRadius: 999,
              padding: "5px 11px",
              whiteSpace: "nowrap",
            }}
          >
            Private &amp; secure
          </span>
        </div>
        <div style={{ padding: "20px 24px" }}>{children}</div>
      </div>
    </div>
  </div>
);

const inputStyle = {
  width: "100%",
  fontSize: 14.5,
  color: "#1F2A37",
  border: "1px solid #E6EAEF",
  borderRadius: 9,
  padding: "10px 12px",
  outline: "none",
  boxSizing: "border-box",
};
const textareaStyle = {
  ...inputStyle,
  minHeight: 96,
  resize: "vertical",
  lineHeight: 1.45,
  fontFamily: "inherit",
};
const btnStyle = {
  fontSize: 14.5,
  fontWeight: 600,
  color: "#fff",
  background: NAVY,
  border: "none",
  borderRadius: 9,
  padding: "11px 20px",
  cursor: "pointer",
};
const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#1F2A37",
  marginBottom: 6,
};
const groupHeaderStyle = {
  fontSize: 11,
  letterSpacing: ".11em",
  textTransform: "uppercase",
  color: "#1E7BC4",
  fontWeight: 600,
  margin: "0 0 10px",
};

// Status chip on the home menu. Colors: Submitted green, In progress blue,
// Not started grey.
const CHIP = {
  Submitted: { color: "#0F6E56", bg: "#EBF7F2", border: "#CBEADD" },
  "In progress": { color: "#1E6FB0", bg: "#EAF3FB", border: "#CFE3F5" },
  "Not started": { color: "#7A8794", bg: "#F2F4F7", border: "#E3E8EE" },
};
const StatusChip = ({ status }) => {
  const c = CHIP[status] || CHIP["Not started"];
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        color: c.color,
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 999,
        padding: "4px 10px",
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
};

// --- read-only video block --------------------------------------------------
const VideoCard = ({ field }) => (
  <div style={{ marginBottom: 14 }}>
    <p style={{ ...labelStyle, marginBottom: 8 }}>{field.label}</p>
    {field.url ? (
      <div
        style={{
          position: "relative",
          paddingTop: "56.25%",
          borderRadius: 10,
          overflow: "hidden",
          background: "#000",
        }}
      >
        <iframe
          src={field.url}
          title={field.label}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
        />
      </div>
    ) : (
      <div
        style={{
          border: "1px dashed #D5DCE4",
          borderRadius: 10,
          padding: "22px 16px",
          textAlign: "center",
          color: "#9AA6B2",
          fontSize: 13.5,
          background: "#FBFCFD",
        }}
      >
        Video coming soon
      </div>
    )}
  </div>
);

// --- one editable field -----------------------------------------------------
// Module-level so its identity is stable across renders (keeps text inputs
// from losing focus mid-keystroke).
function FieldInput({ field, value, onChange, onUpload, uploading }) {
  const { type, options = [] } = field;

  if (type === "video") return <VideoCard field={field} />;

  if (type === "longText") {
    return (
      <textarea
        style={textareaStyle}
        placeholder={field.placeholder}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (type === "single") {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map((opt) => {
          const on = value === opt;
          return (
            <label
              key={opt}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                fontSize: 13.5,
                color: "#1F2A37",
                border: `1px solid ${on ? "#1E7BC4" : "#E6EAEF"}`,
                background: on ? "#EAF3FB" : "#fff",
                borderRadius: 999,
                padding: "7px 13px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name={field.key}
                checked={on}
                onChange={() => onChange(opt)}
                style={{ margin: 0 }}
              />
              {opt}
            </label>
          );
        })}
      </div>
    );
  }

  if (type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) =>
      onChange(arr.includes(opt) ? arr.filter((v) => v !== opt) : [...arr, opt]);
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map((opt) => {
          const on = arr.includes(opt);
          return (
            <label
              key={opt}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                fontSize: 13.5,
                color: "#1F2A37",
                border: `1px solid ${on ? "#1E7BC4" : "#E6EAEF"}`,
                background: on ? "#EAF3FB" : "#fff",
                borderRadius: 9,
                padding: "7px 12px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(opt)}
                style={{ margin: 0 }}
              />
              {opt}
            </label>
          );
        })}
      </div>
    );
  }

  if (type === "repeatable") {
    const arr = Array.isArray(value) ? value : [];
    const addLabel = field.addLabel || "Add";
    // Every edit/add/remove replaces the whole array — setField saves the field
    // as one string[], so a single onChange keeps auto-save consistent.
    const setAt = (i, v) => onChange(arr.map((x, j) => (j === i ? v : x)));
    const removeAt = (i) => onChange(arr.filter((_, j) => j !== i));
    const add = () => onChange([...arr, ""]);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {arr.map((entry, i) => (
          <div
            key={i}
            style={{ display: "flex", gap: 8, alignItems: "center" }}
          >
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={entry || ""}
              onChange={(e) => setAt(i, e.target.value)}
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label={`Remove item ${i + 1}`}
              title="Remove"
              style={{
                flex: "0 0 auto",
                width: 34,
                height: 34,
                lineHeight: 1,
                fontSize: 18,
                color: "#A32D2D",
                background: "#fff",
                border: "1px solid #E6EAEF",
                borderRadius: 9,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          style={{
            alignSelf: "flex-start",
            fontSize: 13.5,
            fontWeight: 600,
            color: "#1E7BC4",
            background: "#EAF3FB",
            border: "1px solid #CFE3F5",
            borderRadius: 9,
            padding: "7px 13px",
            cursor: "pointer",
          }}
        >
          + {addLabel}
        </button>
      </div>
    );
  }

  if (type === "file") {
    if (value && value.name) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            border: "1px solid #CBEADD",
            background: "#EBF7F2",
            borderRadius: 9,
            padding: "9px 12px",
          }}
        >
          <span
            style={{
              fontSize: 13.5,
              color: "#0F6E56",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value.name}
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            style={{
              fontSize: 12.5,
              color: "#A32D2D",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            Remove
          </button>
        </div>
      );
    }
    return (
      <div>
        <input
          type="file"
          disabled={uploading}
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            if (f) onUpload(f);
            e.target.value = ""; // allow re-picking the same file after remove
          }}
          style={{ fontSize: 13.5 }}
        />
        {uploading && (
          <span style={{ fontSize: 12.5, color: "#8A96A3", marginLeft: 8 }}>
            Uploading…
          </span>
        )}
      </div>
    );
  }

  // shortText (default)
  return (
    <input
      style={inputStyle}
      placeholder={field.placeholder}
      value={Array.isArray(value) ? value.join(", ") : (value || "")}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// --- home menu --------------------------------------------------------------
// Module-level for stable identity. `rows` is [{ form, status }].
function FormMenu({ rows, onOpen }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map(({ form, status }) => (
        <button
          key={form.id}
          type="button"
          onClick={() => onOpen(form.id)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            width: "100%",
            textAlign: "left",
            border: "1px solid #E6EAEF",
            background: "#fff",
            borderRadius: 12,
            padding: "13px 15px",
            cursor: "pointer",
          }}
        >
          <span
            style={{ fontSize: 14.5, fontWeight: 600, color: "#1F2A37" }}
          >
            {form.title}
          </span>
          <StatusChip status={status} />
        </button>
      ))}
    </div>
  );
}

export default function ClientPortal() {
  const [params] = useSearchParams();
  const dealId = params.get("d") || "";
  const token = params.get("t") || "";
  const addressFromUrl = params.get("a") || "";

  const [phase, setPhase] = useState("loading"); // loading|gate|home|form|dead|error
  const [brand, setBrand] = useState({
    clientFirstName: "",
    propertyAddress: "",
    side: "",
  });
  const [prefill, setPrefill] = useState({ propertyAddress: "", side: "" });
  // Slice 3 assembly inputs from open/verify: the listing-request type and the
  // clients on the deal (drives formsForRequest + per-client All About You).
  const [formType, setFormType] = useState("");
  const [clients, setClients] = useState([]);
  // Admin-configurable form overrides (portalConfig/formOverrides). Loaded once
  // on mount; {} until it resolves and on any failure, so the page always falls
  // back to the base forms and never blocks or breaks on a missing/denied read.
  const [formOverrides, setFormOverrides] = useState({});
  // Utility-sheet field keys just filled from the address lookup (for the
  // "please verify" hint). Reset whenever a form opens.
  const [autoFilled, setAutoFilled] = useState([]);
  const [answerInput, setAnswerInput] = useState("");
  const [session, setSession] = useState("");
  // Authoritative per-form answers: { [formId]: { [key]: value } }. Updated on
  // every keystroke so status chips stay live and edits survive navigation.
  const [formsMap, setFormsMap] = useState({});
  const [submittedMap, setSubmittedMap] = useState({});
  const [activeFormId, setActiveFormId] = useState("");
  const [saved, setSaved] = useState(true);
  const [uploadingKey, setUploadingKey] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  // Local preview mode: the sandbox with no working backend — the gate accepts
  // any input and save/submit/upload succeed locally.
  const [devLocal, setDevLocal] = useState(false);
  // Bumped by the "Try again" button on the error screen to re-run the open.
  const [retryNonce, setRetryNonce] = useState(0);
  const saveTimer = useRef(null);
  // Monotonic edit counter — a save response only flips the chip to "Saved" if
  // no newer keystroke happened while it was in flight.
  const saveSeq = useRef(0);

  useEffect(() => {
    let live = true;
    (async () => {
      if (!dealId || !token) {
        setPhase("dead");
        return;
      }
      const stored = readStoredSession(token);
      const r = await openPortal({
        dealId,
        token,
        sessionToken: stored || undefined,
      });
      if (!live) return;
      if (r.ok && r.resume === true) {
        // Remembered device — skip the gate entirely.
        const s = r.sessionToken || stored;
        setSession(s);
        storeSession(token, s);
        setBrand(r.brand || {});
        setPrefill(r.prefill || {});
        setFormType(r.formType || "");
        setClients(normalizeClients(r.clients));
        setFormsMap(r.forms || {});
        setSubmittedMap(r.submitted || {});
        setPhase("home");
      } else if (r.ok) {
        if (stored) clearStoredSession(token); // remembered session went stale
        setBrand(r.brand || {});
        // open may already carry the assembly inputs (contract) — keep them so
        // they survive the gate even if verify doesn't resend.
        if (r.formType) setFormType(r.formType);
        if (r.clients) setClients(normalizeClients(r.clients));
        setPhase("gate");
      } else if (IS_DEV) {
        setDevLocal(true);
        setBrand({
          clientFirstName: "Sarah",
          propertyAddress: addressFromUrl || "123 Maple Dr",
          side: "seller", // sandbox: assemble the seller set for two clients
        });
        setPrefill({ propertyAddress: addressFromUrl || "123 Maple Dr" });
        setFormType(""); // seller base set
        setClients(normalizeClients(DEMO_CLIENTS));
        setPhase("gate");
      } else if (isUnreachable(r) || r.status >= 500) {
        // Flaky connection or a backend hiccup — the link itself may be fine.
        // Never show the permanent dead-link page for a transient failure.
        setPhase("error");
      } else {
        setPhase("dead");
      }
    })();
    return () => {
      live = false;
    };
  }, [dealId, token, addressFromUrl, retryNonce]);

  // Don't leave a debounced save firing after unmount.
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  // Load the admin form overrides once. Non-sensitive, world-readable doc;
  // loadPortalFormOverrides never throws (returns {} on missing/error) so a
  // failed read simply leaves the base forms untouched.
  useEffect(() => {
    let live = true;
    loadPortalFormOverrides().then((ov) => {
      if (live) setFormOverrides(ov || {});
    });
    return () => {
      live = false;
    };
  }, []);

  // The device session was rejected — forget it and gently re-ask. formsMap is
  // deliberately left alone so nothing typed is lost.
  const bounceToGate = () => {
    // Kill any pending debounced save — its 403 would call bounceToGate again.
    clearTimeout(saveTimer.current);
    clearStoredSession(token);
    setSession("");
    setSaved(true);
    setUploadingKey("");
    setAnswerInput("");
    setNotice("Please confirm your last name again — your answers are safe.");
    setPhase("gate");
  };

  const doVerify = async (e) => {
    if (e) e.preventDefault();
    const answer = answerInput.trim();
    if (!answer) {
      setErr("Please enter your last name.");
      return;
    }
    setErr("");
    setVerifying(true);
    const r = await verifyPortal({ dealId, token, answer });
    setVerifying(false);
    if (r.ok) {
      const s = r.sessionToken || "dev";
      setSession(s);
      storeSession(token, s);
      setBrand((b) => ({ ...b, ...(r.brand || {}) }));
      setPrefill(r.prefill || {});
      if (r.formType !== undefined) setFormType(r.formType || "");
      if (r.clients !== undefined) setClients(normalizeClients(r.clients));
      // Fold the server's saved answers under anything typed on this device.
      setFormsMap((local) => mergeForms(r.forms || {}, local));
      setSubmittedMap(r.submitted || {});
      setNotice("");
      setPhase("home");
    } else if (IS_DEV) {
      setDevLocal(true);
      setSession("dev");
      setPrefill({ propertyAddress: brand.propertyAddress || "" });
      setFormType("");
      setClients(normalizeClients(DEMO_CLIENTS));
      setNotice("");
      setPhase("home");
    } else if (r.status === 404) {
      // The link was revoked/expired between open and verify.
      setPhase("dead");
    } else {
      setErr(r.error || "That doesn't match — please try again.");
    }
  };

  // The ordered forms for this deal. With clients (Slice 3), assemble from the
  // request type; without them (old backend, pre-deploy) fall back to the
  // side-based list so nothing breaks. formsForRequest expands "All About You"
  // into one instance per client, each carrying its own id/title/prefill.
  const side = brand.side || prefill.side || "";
  // Assemble the base forms, then apply the admin overrides. Per-client "All
  // About You" instances resolve their override by BASE id, so they all share
  // the "allAboutYou" override. With no overrides loaded this is an identity
  // pass — the base forms render exactly as before.
  const assembled = applyOverridesAll(
    clients.length
      ? formsForRequest({ formType, side, clients })
      : formsForSide(side),
    formOverrides,
  );
  // Resolve a form id to its assembled instance (name-titled, with prefill) or
  // fall back to the base registry form for plain ids / deep links — the
  // fallback gets the same override applied (by base id) for consistency.
  const resolveForm = (id) =>
    assembled.find((f) => f.id === id) ||
    applyOverrides(
      getForm(id),
      formOverrides[String(id || "").split(":")[0]],
    );

  // Display answers for a form: server/local map over blanks, seeded with the
  // instance's prefill (client contact fields) and a light property-address
  // prefill for the address field common to several forms. Saved answers always
  // win over prefill.
  const answersFor = (formId) => {
    const form = resolveForm(formId);
    if (!form) return {};
    const seeded = {
      ...blankAnswers(form),
      ...(form.prefill || {}),
      ...(formsMap[formId] || {}),
    };
    if ("propertyAddress" in seeded && !seeded.propertyAddress) {
      seeded.propertyAddress =
        prefill.propertyAddress || brand.propertyAddress || "";
    }
    return seeded;
  };

  const persist = async (formId, nextAnswers, sessionToken, seq) => {
    const r = await savePortal({
      dealId,
      token,
      sessionToken,
      formId,
      answers: nextAnswers,
    });
    if (r.ok || devLocal || (IS_DEV && isUnreachable(r))) {
      // Only claim "Saved" if nothing was typed while this save was in flight.
      if (seq === saveSeq.current) setSaved(true);
      return;
    }
    if (isSessionError(r)) {
      bounceToGate();
      return;
    }
    // Transient failure — leave the chip on "Saving…"; the next keystroke (or
    // submit) retries with the full answers map.
  };

  // Set one field of the active form, then debounce a save of the whole form.
  const setField = (key, value) => {
    const next = { ...answersFor(activeFormId), [key]: value };
    setFormsMap((m) => ({ ...m, [activeFormId]: next }));
    setSaved(false);
    saveSeq.current += 1;
    const seq = saveSeq.current;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(
      () => persist(activeFormId, next, session, seq),
      900,
    );
  };

  const onUpload = async (field, file) => {
    setErr("");
    setUploadingKey(field.key);
    const r = await uploadPortalFile({
      dealId,
      token,
      sessionToken: session,
      formId: activeFormId,
      fieldKey: field.key,
      file,
    });
    setUploadingKey("");
    if (r.ok) {
      const answer = { name: r.name || file.name };
      if (r.driveFileId) answer.driveFileId = r.driveFileId;
      if (r.storagePath) answer.storagePath = r.storagePath;
      setField(field.key, answer);
      return;
    }
    if (devLocal || (IS_DEV && isUnreachable(r))) {
      setField(field.key, { name: file.name }); // local preview stand-in
      return;
    }
    if (isSessionError(r)) {
      bounceToGate();
      return;
    }
    setErr(r.error || "Couldn't upload that file — please try again.");
  };

  // Write any pending debounced edit for the active form NOW, before we
  // navigate away — otherwise the next form's setField clears this timer and
  // the client silently loses their last <900ms of typing (breaking the
  // "saves as you go / finish later" promise).
  const flushPendingSave = () => {
    clearTimeout(saveTimer.current);
    if (activeFormId && !saved) {
      persist(activeFormId, answersFor(activeFormId), session, saveSeq.current);
    }
  };

  // Opening the Utility Information Sheet with empty area fields: try an address
  // lookup and fill only the still-empty matching fields (never overwrite typed
  // answers). No-op unless a provider is configured (see addressLookup.js).
  const maybeAutofillArea = async (id) => {
    if (getForm(id)?.id !== "utilityInfoSheet") return;
    const current = answersFor(id);
    const emptyTargets = AREA_AUTOFILL_KEYS.filter(
      (k) => k in current && !answerFilled(current[k]),
    );
    if (!emptyTargets.length) return;
    const address = prefill.propertyAddress || brand.propertyAddress || "";
    if (!address) return;
    const info = await lookupAreaInfo(address); // resolves to {} in stub mode
    if (!info || typeof info !== "object") return;
    const next = { ...current };
    const filled = [];
    for (const k of emptyTargets) {
      if (typeof info[k] === "string" && info[k].trim() !== "") {
        next[k] = info[k].trim();
        filled.push(k);
      }
    }
    if (!filled.length) return;
    setFormsMap((m) => ({ ...m, [id]: next }));
    setAutoFilled(filled);
    // Persist the autofill like any edit so it survives a refresh.
    setSaved(false);
    saveSeq.current += 1;
    persist(id, next, session, saveSeq.current);
  };

  const openForm = (id) => {
    flushPendingSave();
    setActiveFormId(id);
    setErr("");
    setNotice("");
    setAutoFilled([]);
    setSaved(true);
    setPhase("form");
    maybeAutofillArea(id);
  };

  const backToHome = () => {
    flushPendingSave();
    setErr("");
    setActiveFormId("");
    setAutoFilled([]);
    setPhase("home");
  };

  const doSubmit = async () => {
    const formId = activeFormId;
    const answers = answersFor(formId);
    setErr("");
    clearTimeout(saveTimer.current); // submit carries the latest answers itself
    setSubmitting(true);
    const r = await submitPortal({
      dealId,
      token,
      sessionToken: session,
      formId,
      answers,
    });
    setSubmitting(false);
    if (r.ok || devLocal || (IS_DEV && isUnreachable(r))) {
      // Keep the submitted answers in the local map so the form reopens filled.
      setFormsMap((m) => ({ ...m, [formId]: answers }));
      setSubmittedMap((m) => ({ ...m, [formId]: true }));
      setSaved(true);
      setActiveFormId("");
      setNotice(`Thanks! Your "${getForm(formId)?.title}" was sent to your agent.`);
      setPhase("home");
      return;
    }
    if (isSessionError(r)) {
      bounceToGate();
      return;
    }
    setErr(r.error || "Couldn't submit — please try again.");
  };

  if (phase === "loading") {
    return (
      <Shell>
        <p>Loading…</p>
      </Shell>
    );
  }

  if (phase === "dead") {
    return (
      <Shell>
        <h2 style={{ marginTop: 0, color: "#1F2A37" }}>
          This link is no longer active
        </h2>
        <p>Please ask your Team Edelstein agent to resend your link.</p>
      </Shell>
    );
  }

  if (phase === "error") {
    return (
      <Shell>
        <h2 style={{ marginTop: 0, color: "#1F2A37" }}>We couldn't connect</h2>
        <p>
          Something got in the way — likely a weak connection. Your link is
          still good; please try again in a moment.
        </p>
        <button
          style={btnStyle}
          onClick={() => {
            setPhase("loading");
            setRetryNonce((n) => n + 1);
          }}
        >
          Try again
        </button>
      </Shell>
    );
  }

  if (phase === "gate") {
    return (
      <Shell>
        <p
          style={{
            fontSize: 11,
            letterSpacing: ".12em",
            textTransform: "uppercase",
            color: "#8A96A3",
            margin: "0 0 10px",
          }}
        >
          {notice ? "Quick check" : "First time here"}
        </p>
        {notice && (
          <p style={{ fontSize: 13.5, color: "#1E7BC4", margin: "0 0 12px" }}>
            {notice}
          </p>
        )}
        <form onSubmit={doVerify}>
          <label style={{ display: "block", fontSize: 13.5, marginBottom: 5 }}>
            Confirm it's you — your last name
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <input
              style={inputStyle}
              value={answerInput}
              onChange={(e) => setAnswerInput(e.target.value)}
              autoFocus
            />
            <button
              type="submit"
              style={{
                ...btnStyle,
                ...(verifying ? { opacity: 0.6, cursor: "default" } : {}),
              }}
              disabled={verifying}
            >
              {verifying ? "Checking…" : "Continue"}
            </button>
          </div>
        </form>
        {err && (
          <p style={{ color: "#A32D2D", fontSize: 13, marginTop: 8 }}>{err}</p>
        )}
        <p style={{ fontSize: 12.5, color: "#8A96A3", marginTop: 10 }}>
          Asked once on this device — then we remember you for the year.
        </p>
      </Shell>
    );
  }

  // --- HOME: the menu of forms for this deal's side -------------------------
  if (phase === "home") {
    const rows = assembled.map((form) => ({
      form,
      status: submittedMap[form.id]
        ? "Submitted"
        : formHasProgress(form, formsMap[form.id])
          ? "In progress"
          : "Not started",
    }));
    const submittedCount = rows.filter((r) => r.status === "Submitted").length;
    // Read-only forms (e.g. the Video Library) have no Submit, so they must not
    // count toward the "X of N submitted" total — else it never reaches N.
    const submittableCount = rows.filter(
      (r) => !r.form.fields.every((f) => f.type === "video"),
    ).length;
    return (
      <Shell>
        <h2 style={{ margin: "0 0 4px", fontSize: 22, color: "#1F2A37" }}>
          Welcome{brand.clientFirstName ? `, ${brand.clientFirstName}` : ""}!
        </h2>
        <p style={{ margin: "0 0 6px", fontSize: 14 }}>
          Choose a form below to fill out. Everything saves as you go, and you
          can come back to this link anytime.
        </p>
        {notice && (
          <p
            style={{
              fontSize: 12.5,
              color: "#0F6E56",
              background: "#EBF7F2",
              border: "1px solid #CBEADD",
              borderRadius: 9,
              padding: "8px 12px",
              margin: "0 0 12px",
            }}
          >
            {notice}
          </p>
        )}
        <p style={{ fontSize: 12.5, color: "#8A96A3", margin: "0 0 14px" }}>
          {submittedCount} of {submittableCount} submitted
        </p>
        <FormMenu rows={rows} onOpen={openForm} />
      </Shell>
    );
  }

  // --- FORM: the active form ------------------------------------------------
  const form = resolveForm(activeFormId);
  if (!form) {
    // Deep link to an unknown form id, or state got out of sync — go home.
    return (
      <Shell>
        <button
          type="button"
          onClick={backToHome}
          style={{
            fontSize: 13.5,
            color: "#1E7BC4",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          ← All forms
        </button>
      </Shell>
    );
  }

  const answers = answersFor(activeFormId);
  const alreadySubmitted = Boolean(submittedMap[activeFormId]);
  const readOnly = form.fields.every((f) => f.type === "video");

  const renderField = (f) => {
    if (f.type === "video") return <FieldInput key={f.key} field={f} />;
    return (
      <div key={f.key} style={{ marginBottom: 12 }}>
        <label style={labelStyle}>
          {f.label}
          {f.required && <span style={{ color: "#A32D2D" }}> *</span>}
        </label>
        <FieldInput
          field={f}
          value={answers[f.key]}
          onChange={(v) => setField(f.key, v)}
          onUpload={(file) => onUpload(f, file)}
          uploading={uploadingKey === f.key}
        />
      </div>
    );
  };

  // listOfUpdates reads as 20 rows: each Update N paired with its Approx. year.
  const renderBody = () => {
    if (form.id === "listOfUpdates") {
      const addr = form.fields.find((f) => f.key === "propertyAddress");
      return (
        <div style={{ marginTop: 14 }}>
          {addr && renderField(addr)}
          {Array.from({ length: 20 }, (_, i) => {
            const n = i + 1;
            // Prefer the override-resolved fields (label/required edits from the
            // Portal Forms editor); fall back to the base field so a hidden
            // override never drops a row and crashes the fixed 20-row layout.
            const baseFields = getForm("listOfUpdates").fields;
            const upd =
              form.fields.find((f) => f.key === `update${n}`) ||
              baseFields.find((f) => f.key === `update${n}`);
            const yr =
              form.fields.find((f) => f.key === `updateYear${n}`) ||
              baseFields.find((f) => f.key === `updateYear${n}`);
            return (
              <div
                key={n}
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-end",
                  marginBottom: 12,
                }}
              >
                <div style={{ flex: "1 1 60%" }}>
                  <label style={labelStyle}>{`Update ${n}`}</label>
                  <FieldInput
                    field={upd}
                    value={answers[upd.key]}
                    onChange={(v) => setField(upd.key, v)}
                  />
                </div>
                <div style={{ flex: "1 1 40%" }}>
                  <label style={labelStyle}>Approx. year</label>
                  <FieldInput
                    field={yr}
                    value={answers[yr.key]}
                    onChange={(v) => setField(yr.key, v)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    return groupsOf(form).map((g) => {
      // Only the currently-visible fields in this group. A field hidden by its
      // showIf isn't rendered, and a group with nothing visible drops its
      // header entirely.
      const visible = form.fields.filter(
        (f) => (f.group || "") === g && isFieldVisible(f, answers),
      );
      if (!visible.length) return null;
      return (
        <div key={g || "default"} style={{ marginTop: 14 }}>
          {g && <p style={groupHeaderStyle}>{g}</p>}
          {visible.map((f) => renderField(f))}
        </div>
      );
    });
  };

  return (
    <Shell>
      <button
        type="button"
        onClick={backToHome}
        style={{
          fontSize: 13.5,
          color: "#1E7BC4",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          marginBottom: 10,
        }}
      >
        ← All forms
      </button>
      <h2 style={{ margin: "0 0 4px", fontSize: 22, color: "#1F2A37" }}>
        {form.title}
      </h2>
      <p style={{ margin: "0 0 8px", fontSize: 14 }}>
        {brand.clientFirstName ? `Hi ${brand.clientFirstName} — ` : ""}
        {form.intro}
      </p>
      {alreadySubmitted && (
        <p
          style={{
            fontSize: 12.5,
            color: "#0F6E56",
            background: "#EBF7F2",
            border: "1px solid #CBEADD",
            borderRadius: 9,
            padding: "8px 12px",
            margin: "0 0 8px",
          }}
        >
          Already submitted — you can update it anytime.
        </p>
      )}
      {autoFilled.length > 0 && form.id === "utilityInfoSheet" && (
        <p
          style={{
            fontSize: 12.5,
            color: "#1E6FB0",
            background: "#EAF3FB",
            border: "1px solid #CFE3F5",
            borderRadius: 9,
            padding: "8px 12px",
            margin: "0 0 8px",
          }}
        >
          Auto-filled from your address — please verify.
        </p>
      )}

      {renderBody()}

      {err && <p style={{ color: "#A32D2D", fontSize: 13 }}>{err}</p>}

      {readOnly ? (
        <div
          style={{
            borderTop: "1px solid #F0F3F6",
            paddingTop: 14,
            marginTop: 8,
          }}
        >
          <button style={btnStyle} onClick={backToHome}>
            ← Back to all forms
          </button>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid #F0F3F6",
            paddingTop: 14,
            marginTop: 8,
          }}
        >
          <span style={{ fontSize: 12.5, color: "#0F6E56" }}>
            {saved ? "Saved — you can finish later" : "Saving…"}
          </span>
          <button
            style={{
              ...btnStyle,
              ...(submitting ? { opacity: 0.6, cursor: "default" } : {}),
            }}
            onClick={doSubmit}
            disabled={submitting}
          >
            {submitting
              ? "Sending…"
              : alreadySubmitted
                ? "Resubmit to my agent"
                : "Submit to my agent"}
          </button>
        </div>
      )}
    </Shell>
  );
}
